import { CameraView } from "@/utils/cam-view";
import { assert, TypedEvent } from "@faro-lotv/foundation";
import { IElementImg360 } from "@faro-lotv/ielement-types";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  type JSX,
} from "react";
import { Vector3, Vector3Tuple } from "three";

/** Event data for context menu anchor position */
export type SceneContextMenuAnchorPosition = {
  /** The X coordinate of the mouse pointer in viewport coordinates. */
  x: number;

  /** The Y coordinate of the mouse pointer in viewport coordinates. */
  y: number;

  /** Shift key down state */
  shiftKeyDown?: boolean;
};

/** Data for pano depth info availability */
export type PanoDepthAvailabilityInfo = {
  /** The pano has depth information or not */
  isPanoWithDepth: boolean;

  /** The depth is loaded and ready for use*/
  isDepthReady: boolean;
};

/** Event data for context menu */
export type SceneContextMenuEvent = SceneContextMenuAnchorPosition & {
  // The ID of the CAD object associated with the context menu;
  // It can be undefined in case if event is not associated with CAD
  objectId?: number;

  // clicked point on the CAD/PointCloud/Pano object (in WCS and meters)
  point?: Vector3Tuple;

  // pano depth info availability
  panoDepthInfo?: PanoDepthAvailabilityInfo;
};

/** Event data for set visibility of CAD model */
export type CadModelSetVisibilityEvent = {
  /** The ID of the CAD object */
  objectId: number;

  /** The visibility to be set to the CAD object */
  visibility: boolean;

  /** true if all other objects should have inverse visibility */
  isolate: boolean;
};

/** Event data for placing a pin */
export type PlacePinEvent = {
  // clicked point on the CAD/PointCloud/Pano object (in WCS and meters)
  point: Vector3Tuple;
};

/** Event data for activateSplitSide */
export enum SplitSide {
  left = "left screen",
  right = "right screen",
}

export type SceneEvents = {
  /** Event emitted when invalidate CAD model scene */
  invalidateCadScene: TypedEvent<void>;

  /** Event emitted when invalidate point cloud scene */
  invalidatePointCloudScene: TypedEvent<void>;

  /** Event emitted when invalidate the gaussian splats scene */
  invalidateGaussianSplatsScene: TypedEvent<void>;

  /** Event emitted when user right clicks on the 3D view to popup the context menu on a object */
  openSceneContextMenuFrom3DView: TypedEvent<SceneContextMenuEvent>;

  /**
   * Event emitted from 3D view overlay component when user right click to popup the context menu.
   * Because there is no threejs context from overlay components, this event passes the click position to
   * 3D renderer component in which raycast on mesh can be called to find the relative object.
   */
  openSceneContextMenuFromOverlay: TypedEvent<SceneContextMenuAnchorPosition>;

  /** Event emitted when change CAD model visibility from scene context menu */
  setCadModelVisibility: TypedEvent<CadModelSetVisibilityEvent>;

  /**
   Event emitted when locate CAD object in the model tree from scene context menu
   When the event type is a number it's the id of the cad object to select.
   When it is undefined, it means that we want to deselect all the cad objects
   */
  locateCadObjectInTree: TypedEvent<number | undefined>;

  /** Event emitted when select parent from scene context menu */
  selectParentCadObjectInTree: TypedEvent<number>;

  /** Notify the current scene it should zoom to a specific point */
  lookAt: TypedEvent<Vector3>;

  /** Move to a 360 waypoint the same way the current mode does when the user clicks it */
  goToWaypoint: TypedEvent<IElementImg360>;

  /** A goToWaypoint target no scene has handled yet, e.g. because it was sent during a mode transition */
  pendingGoToWaypoint: { waypoint: IElementImg360 | null };

  /** A pending lookAt target to be consumed by the walk scene once it mounts. */
  pendingLookAt: { target: Vector3 | null };

  /** The camera should animate towards a specific pose */
  setCameraView: TypedEvent<CameraView>;

  /** The camera of the right view in split mode should animate towards a specific pose */
  setRightCameraView: TypedEvent<CameraView>;

  /** Event emitted with shortcut key Shift+Right-click  */
  placePinMarker: TypedEvent<PlacePinEvent>;

  /** Event emitted when clicking 2D photo object in gallery in split mode */
  activatePhotoObjectSplitSide: TypedEvent<SplitSide>;

  /** Take a screenshot */
  takeScreenshot: TypedEvent<void>;
};

/** A context for the 3D scene events */
export const SceneEventsContext = createContext<SceneEvents | undefined>(
  undefined,
);

/**
 * @returns The registered CAD 3D scene events context
 */
export function useSceneEvents(): SceneEvents {
  const ctx = useContext(SceneEventsContext);
  assert(
    ctx,
    "useSceneEvents should be used only inside a SceneEventsContextProvider",
  );
  return ctx;
}

/** The scene events, one instance per scene */
export class SceneEventsContextData implements SceneEvents {
  invalidateCadScene = new TypedEvent<void>();
  invalidatePointCloudScene = new TypedEvent<void>();
  invalidateGaussianSplatsScene = new TypedEvent<void>();
  openSceneContextMenuFrom3DView = new TypedEvent<SceneContextMenuEvent>();
  openSceneContextMenuFromOverlay =
    new TypedEvent<SceneContextMenuAnchorPosition>();
  setCadModelVisibility = new TypedEvent<CadModelSetVisibilityEvent>();
  locateCadObjectInTree = new TypedEvent<number | undefined>();
  selectParentCadObjectInTree = new TypedEvent<number>();
  lookAt = new TypedEvent<Vector3>();
  goToWaypoint = new TypedEvent<IElementImg360>();
  pendingGoToWaypoint: { waypoint: IElementImg360 | null } = { waypoint: null };
  pendingLookAt: { target: Vector3 | null } = { target: null };
  setCameraView = new TypedEvent<CameraView>();
  setRightCameraView = new TypedEvent<CameraView>();
  placePinMarker = new TypedEvent<PlacePinEvent>();
  activatePhotoObjectSplitSide = new TypedEvent<SplitSide>();
  takeScreenshot = new TypedEvent<void>();
}

/**
 * @returns A component to register the invalidate 3D scene event context
 */
export function SceneEventsContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [contextData] = useState(() => new SceneEventsContextData());

  return (
    <SceneEventsContext value={contextData}>{children}</SceneEventsContext>
  );
}
